import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getAvatarUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import RoleService from "src/Services/API/role.service";
import UserService from "src/Services/API/user.service";
import PolicyService from "src/Services/API/policy.service";

const MAX_VISIBLE_USER_CHIPS = 6;

// Real avatar image when available; falls back to an initials circle on missing
// or broken image (mirrors the Avatar pattern in Setting/Team/status/Status).
function UserAvatar({ user, className = "w-5 h-5 text-[10px]" }) {
  const [errored, setErrored] = useState(false);
  if (user?.avatar && !errored) {
    return (
      <img
        src={getAvatarUrl(user.avatar)}
        onError={() => setErrored(true)}
        alt={user?.name ?? ""}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();
  return (
    <span
      className={`shrink-0 rounded-full bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold flex items-center justify-center ${className}`}
    >
      {initial}
    </span>
  );
}

/**
 * Assign Policy modal — attach this policy to one or more roles and/or directly
 * to users. Pre-checks the roles/users already using the policy and saves the
 * full sets via the policy → roles and policy → users endpoints.
 */
function AssignPolicyToRolesModal({ shown, policyId, attachedRoleIds, attachedUserIds, onClose, onSaved }) {
  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showAllChips, setShowAllChips] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!shown) return;
    setSelectedRoleIds(attachedRoleIds ?? []);
    setSelectedUserIds(attachedUserIds ?? []);
    setUserSearch("");
    setShowAllChips(false);
    // The pickers only need role id/name and user display fields, so use the
    // auth-only role list and the ungated user picker endpoint — the manage
    // list (iam:readRole) and full user listing (user:read) would 403 policy
    // managers without those permissions.
    RoleService.getAll()
      .then((res) => setRoles(res.data ?? []))
      .catch(handleError);
    UserService.getUsersForAssign(null)
      .then((res) => setUsers(res.data ?? []))
      .catch(handleError);
  }, [shown, attachedRoleIds, attachedUserIds]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedUserIds.includes(u.id)),
    [users, selectedUserIds],
  );

  const userMatches = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) => !selectedUserIds.includes(u.id))
      .filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [users, userSearch, selectedUserIds]);

  if (!shown) return null;

  const toggleRole = (id) =>
    setSelectedRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addUser = (id) => {
    setSelectedUserIds((prev) => [...prev, id]);
    setUserSearch("");
  };

  const removeUser = (id) => setSelectedUserIds((prev) => prev.filter((x) => x !== id));

  const save = () => {
    setIsSaving(true);
    Promise.all([
      PolicyService.assignToRoles(policyId, selectedRoleIds),
      PolicyService.assignToUsers(policyId, selectedUserIds),
    ])
      .then(() => {
        toast("Policy assignments updated");
        onSaved?.();
        onClose?.();
      })
      .catch(handleError)
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-[12px] w-[784px] max-w-[95vw] max-h-[92vh] flex flex-col shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#EEF0F9] flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="text-[20px] leading-7 font-OpenSansSemiBold text-[#1A2332] capitalize">Assign Policy</h2>
            <p className="text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#7C829A]">
              Attach this policy bundle to one or more roles or users.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-8 overflow-y-auto flex-1 flex flex-col gap-8">
          {/* Assign to Roles */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
              Assign To Roles
            </p>
            <div className="flex flex-wrap gap-3">
              {roles.length === 0 ? (
                <p className="text-[13px] font-OpenSansRegular text-[#868DA6] py-2">No roles found.</p>
              ) : (
                roles.map((r) => {
                  const checked = selectedRoleIds.includes(r.id);
                  return (
                    <label
                      key={r.id}
                      className={`flex-1 min-w-[260px] flex items-center gap-3 px-4 py-3 rounded-[8px] outline outline-1 -outline-offset-1 cursor-pointer ${
                        checked ? "outline-[#0065FD] bg-[#F5F9FF]" : "outline-[#E5E8F3] hover:outline-[#B9C4E0]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(r.id)}
                        className="accent-[#0065FD] w-4 h-4"
                      />
                      <span
                        className={`text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold capitalize ${
                          checked ? "text-[#0065FD]" : "text-[#1A2332]"
                        }`}
                      >
                        {r.name}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Assign to Users */}
          <div className="flex flex-col gap-1">
            <p className="flex items-center gap-2 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
              Assign To Users
              <span className="text-[#868DA6] font-OpenSansRegular normal-case">(Optional)</span>
            </p>
            <div className="relative min-h-[46px] rounded-[8px] outline outline-1 -outline-offset-1 outline-[#E5E7EB] px-3 py-2 flex flex-wrap items-center gap-2">
              {(showAllChips ? selectedUsers : selectedUsers.slice(0, MAX_VISIBLE_USER_CHIPS)).map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1.5 pl-1 pr-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#5F6477]"
                >
                  <UserAvatar user={u} />
                  {u.name}
                  <button
                    onClick={() => removeUser(u.id)}
                    className="text-[#868DA6] hover:text-[#D93A3A] flex items-center"
                    title="Remove"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M9 3L3 9M3 3l6 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </span>
              ))}
              {!showAllChips && selectedUsers.length > MAX_VISIBLE_USER_CHIPS && (
                <button
                  onClick={() => setShowAllChips(true)}
                  className="inline-flex px-2.5 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#5F6477] hover:bg-[#E5E8F3]"
                >
                  +{selectedUsers.length - MAX_VISIBLE_USER_CHIPS} more
                </button>
              )}
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder={selectedUsers.length ? "Search users…" : "Search users to assign…"}
                className="flex-1 min-w-[140px] py-1 text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6] outline-none bg-transparent"
              />
              {userMatches.length > 0 && (
                <div className="absolute left-0 right-0 bottom-full mb-1 z-[70] bg-white outline outline-1 -outline-offset-1 outline-[#E5E8F3] rounded-[8px] shadow-[0px_8px_24px_rgba(16,24,40,0.12)] max-h-[220px] overflow-y-auto">
                  {userMatches.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => addUser(u.id)}
                      className="w-full flex items-center gap-2.5 text-left px-3 py-2 hover:bg-[#F8F9FE]"
                    >
                      <UserAvatar user={u} className="w-7 h-7 text-[12px]" />
                      <span className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332]">{u.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E8F3] flex items-center justify-end gap-6">
          <button
            onClick={onClose}
            className="h-10 px-1 rounded-[6px] text-[16px] leading-6 capitalize font-OpenSansSemiBold text-[#7C829A] hover:text-[#1A2332]"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={isSaving}
            className="h-10 px-4 rounded-[8px] bg-[#0065FD] text-white text-[16px] leading-6 capitalize font-OpenSansSemiBold hover:bg-[#0048bd] disabled:opacity-60"
          >
            {isSaving ? "Saving…" : "Assign Policy"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignPolicyToRolesModal;
